import { statementCsv } from '@drbell/core';
import { useEffect, useState } from 'react';
import { api, ApiError, type PatientSummary, type Statement as StatementData } from '../api.ts';

/**
 * The statement — everything that happened, in order, like a bank statement.
 *
 * For one record (doses, medicines, who was invited and who answered, the
 * alerts DrBell sent) or for this account (consents, contact changes, the
 * invitations it answered). Every word arrives rendered, in the reader's own
 * language, from the server: the phone, this page and the printed copy say
 * the same thing because only one place writes it.
 *
 * Printing prints the server's own page, not this one. A PDF of a React
 * screen carries its buttons, its theme and whatever the browser decides to
 * do with a wide table; the server's page is built for paper.
 */
export type StatementScope = { kind: 'record'; patient: PatientSummary } | { kind: 'account' };

const DAY = 24 * 60 * 60 * 1000;
const STEP = 30 * DAY;

function load(scope: StatementScope, from?: number, to?: number): Promise<StatementData> {
  const q = from !== undefined && to !== undefined ? `?from=${from}&to=${to}` : '';
  return scope.kind === 'record'
    ? api.get<StatementData>(`/api/patients/${scope.patient.id}/statement${q}`)
    : api.get<StatementData>(`/api/me/statement${q}`);
}

export function Statement({ scope, onBack }: { scope: StatementScope; onBack: () => void }) {
  /** Oldest first, each one a period the server rendered. */
  const [periods, setPeriods] = useState<StatementData[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The plan keeps nothing older, so "Earlier" has nothing to show. */
  const [atStart, setAtStart] = useState(false);
  const [printing, setPrinting] = useState(false);

  /*
   * Keyed by whose statement it is, not by the scope object: the parent
   * builds a fresh one on every render, and depending on that refetched the
   * statement — and threw away every "Earlier" page — each time it did.
   */
  const scopeKey = scope.kind === 'record' ? scope.patient.id : 'me';
  useEffect(() => {
    let live = true;
    setPeriods(null);
    setError(null);
    setAtStart(false);
    load(scope)
      .then((s) => { if (live) setPeriods([s]); })
      .catch((e: unknown) => { if (live) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const earlier = async () => {
    if (!periods?.[0]) return;
    const to = periods[0].period.from;
    const from = to - STEP;
    setBusy(true);
    setError(null);
    try {
      const older = await load(scope, from, to);
      /*
       * The server clamps `from` to what the record's plan keeps. A window
       * that came back shorter than asked is the edge of the history: show
       * what it has, and offer nothing further back.
       */
      const clamped = older.period.from > from + 60_000;
      if (older.period.to - older.period.from <= 60_000) {
        setAtStart(true);
        return;
      }
      // Both ends are inclusive, so a line exactly on the seam would show twice.
      const seen = new Set(periods.flatMap((p) => p.lines.map((l) => l.id)));
      const fresh = { ...older, lines: older.lines.filter((l) => !seen.has(l.id)) };
      setPeriods([fresh, ...periods]);
      if (clamped) setAtStart(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    if (!periods?.length) return;
    const from = periods[0]!.period.from;
    const to = periods[periods.length - 1]!.period.to;
    const q = `?from=${from}&to=${to}&format=html`;
    const url = scope.kind === 'record'
      ? `/api/patients/${scope.patient.id}/statement${q}`
      : `/api/me/statement${q}`;
    setPrinting(true);
    setError(null);
    try {
      await printPage(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPrinting(false);
    }
  };

  const back = (
    <button onClick={onBack} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)">
      ← Back
    </button>
  );

  if (!periods) {
    return (
      <section className="space-y-3">
        {back}
        {error
          ? <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500">{error}</p>
          : <p className="text-(--muted)">Loading…</p>}
      </section>
    );
  }

  const latest = periods[periods.length - 1]!;

  return (
    <section className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {back}
        <button
          disabled={printing}
          onClick={() => void print()}
          className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent) disabled:opacity-40"
        >
          {printing ? 'Preparing…' : 'Print or save as PDF'}
        </button>
        <button
          onClick={() => {
            // The lines showing, as a spreadsheet file (docs/33 B11).
            const csv = statementCsv(['When', 'What happened', 'By', 'From'], periods.flatMap((p) => p.lines));
            const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'drbell-statement.csv';
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
          className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          Download as a spreadsheet (CSV)
        </button>
      </div>

      <header>
        <h2 className="text-lg font-semibold">{latest.title}</h2>
        {latest.subject && <p className="text-sm">{latest.subject}</p>}
      </header>

      {atStart ? (
        <p className="text-center text-xs text-(--muted)">Nothing earlier is kept.</p>
      ) : (
        <button
          disabled={busy}
          onClick={() => void earlier()}
          className="min-h-11 w-full rounded-lg border border-dashed border-(--line) px-4 text-sm hover:border-(--accent) disabled:opacity-40"
        >
          {busy ? 'Loading…' : 'Earlier'}
        </button>
      )}

      {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>}

      {periods.map((p) => <Period key={`${p.period.from}-${p.period.to}`} data={p} />)}

      <p className="text-xs text-(--muted)">{latest.footer}</p>
    </section>
  );
}

/** One period: its own label and totals, then its lines, oldest first. */
function Period({ data }: { data: StatementData }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-(--line) bg-(--panel) p-3">
        <p className="text-sm font-medium">{data.period.label}</p>
        {data.summary.labels.length > 0 && (
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {data.summary.labels.map((l) => <span key={l}>{l}</span>)}
          </p>
        )}
      </div>

      {data.truncated && (
        <p role="alert" className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
          There is more in this period than one statement can hold, so some lines are missing.
        </p>
      )}

      {data.lines.length === 0 ? (
        <p className="rounded-xl border border-dashed border-(--line) p-6 text-center text-sm text-(--muted)">{data.empty}</p>
      ) : (
        <>
          {/* A table where there is room for one… */}
          <table className="hidden w-full border-collapse text-sm sm:table">
            <thead>
              <tr className="border-b border-(--line) text-left text-xs uppercase tracking-wide text-(--muted)">
                <th className="py-2 pr-3 font-medium">{data.columns.date}</th>
                <th className="py-2 pr-3 font-medium">{data.columns.what}</th>
                <th className="py-2 pr-3 font-medium">{data.columns.by}</th>
                <th className="py-2 font-medium">{data.columns.from}</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.id} className="border-b border-(--line) align-top">
                  <td className="whitespace-nowrap py-2 pr-3 text-(--muted)">{l.when}</td>
                  <td className="py-2 pr-3">{l.text}</td>
                  <td className="py-2 pr-3">{l.by}</td>
                  <td className="py-2 text-(--muted)">{l.from}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* …and stacked rows on a phone, where four columns would scroll
              sideways or crush "What happened" to a word per line. */}
          <ul className="divide-y divide-(--line) rounded-xl border border-(--line) sm:hidden">
            {data.lines.map((l) => (
              <li key={l.id} className="px-3 py-2 text-sm">
                <p className="text-xs text-(--muted)">{l.when}</p>
                <p className="break-words">{l.text}</p>
                <p className="text-xs text-(--muted)">
                  {[l.by && `${data.columns.by}: ${l.by}`, l.from && `${data.columns.from}: ${l.from}`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const IOS = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/*
 * Print the server's printable page.
 *
 * Fetched first rather than pointed at, so a refusal (signed out, access
 * removed) is an error on this screen instead of a printed page of JSON.
 *
 * Then printed from a hidden frame: same origin, so this page may call its
 * print(), and the dialog previews the statement and nothing else. Except
 * on iPhone and iPad, where printing from a frame has printed the page
 * around it instead; there it opens in a tab of its own, and prints from
 * there (the tab is also where Share → Save to Files finds it). The tab is
 * opened before the fetch, while the press still counts as the person's
 * own: a tab opened after an await is a pop-up, and blocked.
 */
let lastFrame: HTMLIFrameElement | null = null;

async function printPage(url: string): Promise<void> {
  const tab = IOS() ? window.open('', '_blank') : null;
  let html: string;
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error(`Could not prepare the statement (${response.status}).`);
    html = await response.text();
  } catch (e) {
    tab?.close();
    throw e;
  }

  if (tab) {
    tab.document.open();
    tab.document.write(html);
    tab.document.close();
    tab.focus();
    tab.print();
    return;
  }

  lastFrame?.remove();
  const frame = document.createElement('iframe');
  frame.title = 'Statement for printing';
  frame.setAttribute('aria-hidden', 'true');
  // Off-screen rather than display:none, which some browsers decline to print.
  Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  frame.onload = () => {
    const w = frame.contentWindow;
    if (!w) return;
    w.addEventListener('afterprint', () => frame.remove());
    w.focus();
    w.print();
  };
  frame.srcdoc = html;
  document.body.appendChild(frame);
  lastFrame = frame;
}
